import Decimal from "@/utils/decimal";
import { GQL_PaycheckTaxDeductionMethodEnum } from '@/queries/global';
import dayjs from '@/utils/dayjs';
import uniq from 'lodash/uniq';
import min from 'lodash/min';

import { apolloProvider } from '@/utils/vue-apollo';
import gql from "graphql-tag";
import { GQL_salaryTaxTableTaxDeduction } from "@/queries/__generated__/salaryTaxTableTaxDeduction";
import i18n from "@/i18n";

const SALARY_TAX_TABLE_TAX_DEDUCTION = gql`
  query salaryTaxTableTaxDeduction(
    $taxTable: String!,
    $amount: Decimal!,
    $year: Int!,
  ) {
    salaryTaxTableTaxDeduction(taxTable: $taxTable, amount: $amount, year: $year) {
      amount
      error
    }
  }
`;

export async function fetchSalaryTaxTableTaxDeduction(
  {
    taxTable,
    amount,
    year,
  }: {
    taxTable: string;
    amount: Decimal;
    year: number;
  },
): Promise<{amount: Decimal | null; error: string | null}> {
  const apolloClient = apolloProvider.defaultClient;

  const response = await apolloClient
    .query<GQL_salaryTaxTableTaxDeduction>({
      query: SALARY_TAX_TABLE_TAX_DEDUCTION,
      variables: {
        taxTable,
        amount,
        year,
      },
      fetchPolicy: `no-cache`,
    });

  return response.data.salaryTaxTableTaxDeduction;
}

interface ITaxDeductionPaycheck {
  taxTable: string | null;
  journalDate: string;
  taxPercentage: Decimal | null;
  taxExemptionAmountLimit: Decimal | null;
  paycheckLines: ITaxDeductionPaycheckLine[];
}

interface ITaxDeductionPaycheckLine {
  sum: Decimal;
  taxable: boolean;
  vacationMoneyEarnedInPreviousYear: boolean;
  number: number;
  _destroy?: boolean;
}

interface IPaycheckSum {
  number: number;
  sum: Decimal;
}

export type TaxDeductionCalculation = {
  kind: `calculation`;

  // taxable amount, income should be employee taxed
  taxableAmount: Decimal;
  // taxable amount rounded down to whole 100
  originalTaxableAmount: Decimal;
  taxableIncomeThisYear?: Decimal;
  taxableIncomeThisPaycheck?: Decimal;
  frikortAmountLimit?: Decimal;
  taxDeduction: Decimal;

  // possible error during calculation
  error: string | null;
}
type CantCalculateForNegative = {
  kind: `cant_calculate_for_negative`;
  // we can't calculate for tax deduction for negative amount. Or we decided not
  // to do it because it is hard to know exactly what to do. We could send
  // hours thinking and trying but it happens very rarely that we have negative
  // paycheck. In those rare occurrence you want ho have exactly the same
  // amount of tax deduction.
};

export type TaxDeductionCalculationResult = TaxDeductionCalculation | CantCalculateForNegative;

export const taxCalculator = ((paycheck: ITaxDeductionPaycheck, paycheckSums: IPaycheckSum[]) => {
  function notDeletedLines() {
    return paycheck.paycheckLines.filter(line => !line._destroy);
  }

  function calcTaxableAmount(): Decimal {
    return notDeletedLines()
      .filter(line => line.taxable && !line.vacationMoneyEarnedInPreviousYear)
      .reduce((sum, line) => sum.add(line.sum), new Decimal(0));
  }

  function roundDownToClosestHundred(amount: Decimal): Decimal {
    return amount.minus(amount.modulo(100));
  }

  function sumThisYear(lineNumber: number): Decimal {
    const paycheckSum = paycheckSums.find(sum => sum.number === lineNumber);
    return (paycheckSum?.sum  || new Decimal(0)).add(
      notDeletedLines()
        .filter(line => line.number === lineNumber)
        .reduce((sum, line) => sum.add(line.sum), new Decimal(0)),
    );
  }

  async function calculateTaxDeductionByTableTax(): Promise<TaxDeductionCalculationResult> {
    const taxableAmount = calcTaxableAmount();
    if (taxableAmount.lessThan(0)) {
      return {kind: `cant_calculate_for_negative`};
    }

    const originalTaxableAmount = roundDownToClosestHundred(taxableAmount);
    const year = dayjs.strict(paycheck.journalDate).year();

    if (!paycheck.taxTable) {
      return {
        kind: `calculation`,
        taxableAmount,
        originalTaxableAmount,
        taxDeduction: new Decimal(0),
        error: i18n.t(`Paycheck.taxCardTaxTableNotSet`) as string,
      };
    }

    const taxDeduction = await fetchSalaryTaxTableTaxDeduction({year, amount: originalTaxableAmount, taxTable: paycheck.taxTable });

    return {
      kind: `calculation`,
      taxableAmount,
      originalTaxableAmount,
      taxDeduction: taxDeduction.amount || new Decimal(0),
      error: taxDeduction.error && i18n.t(
        `Paycheck.TaxDeductionTableCalculatorErrors.${taxDeduction.error}`,
        {taxTable: paycheck.taxTable, year},
      ) as string,
    };
  }

  async function calculateDeductionByTaxPercent(): Promise<TaxDeductionCalculationResult> {
    const taxableAmount = calcTaxableAmount();

    if (taxableAmount.lessThan(0)) {
      return {kind: `cant_calculate_for_negative`};
    }
    const originalTaxableAmount = roundDownToClosestHundred(taxableAmount);

    if (!paycheck.taxPercentage) {
      return {
        kind: `calculation`,
        taxableAmount,
        originalTaxableAmount,
        taxDeduction: new Decimal(0),
        error: i18n.t(`Paycheck.taxCardTaxPercentageNotSet`) as string,
      };
    }

    const taxDeduction = originalTaxableAmount.times(paycheck.taxPercentage).dividedBy(100);

    return {
      kind: `calculation`,
      taxableAmount,
      originalTaxableAmount,
      taxDeduction,
      error: null,
    };
  }

  function calculateTaxDeductionForTaxExemption(): TaxDeductionCalculationResult {
    const taxableIncomeThisPaycheck = calcTaxableAmount();
    if (taxableIncomeThisPaycheck.lessThan(0)) {
      return {kind: `cant_calculate_for_negative`};
    }

    const soFarThisYear = uniq(
      notDeletedLines()
        .filter(line => line.taxable && !line.vacationMoneyEarnedInPreviousYear)
        .map(line => line.number),
    ).reduce((sum, number) => sum.plus(sumThisYear(number)), new Decimal(0));
    const overTheLimitThisYear = soFarThisYear.minus(paycheck.taxExemptionAmountLimit || new Decimal(0));

    if (overTheLimitThisYear.greaterThan(0)) {
      const overTheLimitThisPaycheck =
        min([ overTheLimitThisYear, taxableIncomeThisPaycheck ])!;

      return {
        kind: `calculation`,
        taxableAmount: overTheLimitThisPaycheck,
        originalTaxableAmount: overTheLimitThisPaycheck,
        taxableIncomeThisYear: soFarThisYear,
        taxableIncomeThisPaycheck: taxableIncomeThisPaycheck,
        frikortAmountLimit: paycheck.taxExemptionAmountLimit || new Decimal(0),
        taxDeduction: overTheLimitThisPaycheck.dividedBy(2).toDecimalPlaces(0, Decimal.ROUND_DOWN), // 50%
        error: null,
      };
    } else {
      return {
        kind: `calculation`,
        taxableAmount: new Decimal(0),
        originalTaxableAmount: new Decimal(0),
        taxableIncomeThisYear: soFarThisYear,
        taxableIncomeThisPaycheck: taxableIncomeThisPaycheck,
        frikortAmountLimit: paycheck.taxExemptionAmountLimit || new Decimal(0),
        taxDeduction: new Decimal(0),
        error: null,
      };
    }
  }

  async function calculateTaxDeduction(method: GQL_PaycheckTaxDeductionMethodEnum): Promise<TaxDeductionCalculationResult> {
    if (method === 'MAIN_EMPLOYER_TABLE') {
      return calculateTaxDeductionByTableTax();
    } else if (method === 'MAIN_EMPLOYER_PERCENTAGE' || method === 'SECONDARY_EMPLOYER_PERCENTAGE') {
      return calculateDeductionByTaxPercent();
    } else if (method === 'TAX_EXEMPTION') {
      return calculateTaxDeductionForTaxExemption();
    } else {
      throw `Unknown tax deduction method: ${method}`;
    }
  }

  function emptyTaxDeductionCalculation(): TaxDeductionCalculation {
    return {
      kind: `calculation`,
      taxableAmount: new Decimal(0),
      originalTaxableAmount: new Decimal(0),
      taxableIncomeThisYear: new Decimal(0),
      taxableIncomeThisPaycheck: new Decimal(0),
      frikortAmountLimit: new Decimal(0),
      taxDeduction: new Decimal(0),
      error: null,
    };
  }

  return {
    calculateTaxDeduction,
    emptyTaxDeductionCalculation,
  };
});
